import React, { Component, createRef } from 'react';
import { Form, Field, FormSpy } from 'react-final-form';
import pageIconSrc from '../../../assets/images/member-circle.svg';

import { profileAddressField, profileRadioGroup, withError } from '../../forms/newFields';

import { CIVILITY_MR, CIVILITY_MRS, MEMBER_STATUSES } from '../../../constants/backend';

import arrowLeftSrc from '../../../assets/images/arrow-left.svg';
import IndexLink from '../../partials/IndexLink';
import { ReactSVG } from 'react-svg';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  messagesSelector,
  userDataSelector,
  currentThemeSelector,
  isProfileIncompleteUserSelector,
  personalDetailsConfigSelector,
  personalDetailsSubscribeConfigSelector,
  acceptTermsInitialValuesSelector
} from '../../../helpers/selectors';
import { getMessage as t, safe } from '../../../utils/utils';
import classnames from 'classnames';
import _forEach from 'lodash/forEach';
import { PureTextField } from '../fields/text';
import { scrollToErrorInfo } from '../../../helpers/scrollToError';
import { PureDateField } from '../fields/datePicker';
import { getLocationProp } from '../../partials/LocationInput/utils';
import { PurePhoneNumberField } from '../../account/fields/phoneNumber';
import { checkPhoneNumber } from '../../../helpers';
import { getKeycloakEmail } from '../../../utils/keycloak';
import AcceptTermsForm from '../AcceptTermsForm';
import { compose } from 'redux';

const profileHonorificOptions = (m) => [
  {
    value: CIVILITY_MR,
    title: t(m, 'generic.mr')
  },
  {
    value: CIVILITY_MRS,
    title: t(m, 'generic.mrs')
  }
];

const { TO_COMPLETE, NOT_APPROVED, TO_REVIEW } = MEMBER_STATUSES;
const notApprovedStatuses = new Set([TO_COMPLETE, NOT_APPROVED, TO_REVIEW]);

const fieldEnabled = (userStatus) => {
  const notApprovedStatus = notApprovedStatuses.has(userStatus);

  return {
    civility: notApprovedStatus,
    lastName: notApprovedStatus,
    firstName: notApprovedStatus,
    address: true,
    city: true,
    postalCode: true,
    country: true,
    phoneNumber: true,
    birthDate: notApprovedStatus,
    login: false
  };
};

function isReadOnlyField(name, props) {
  const { isSubscribePage, userData, isProfileIncompleteUser } =
    props;
  const { status: userStatus } = userData || {};
  const subscribeCheck = !(isSubscribePage && !isProfileIncompleteUser);

  return subscribeCheck && !fieldEnabled(userStatus)[name];
}

function configSubscribeHide(name, props) {
  return (
    props.isSubscribePage && safe(() => props.brandCompanySubscribeConfig[name].hide)
  );
}

function configHide(name, props) {
  return !props.isSubscribePage && safe(() => props.brandCompanyConfig[name].hide);
}

function validate(values, props) {
  const { address, country, phoneNumber } = values || {};
  const { m } = props;
  const errors = {};

  const mandatoryFields = ['lastName', 'firstName', 'city', 'postalCode', 'birthDate'];

  _forEach(mandatoryFields, (fieldName) => {
    if (
      !values[fieldName] &&
      !isReadOnlyField(fieldName, props) &&
      !configSubscribeHide(fieldName, props) &&
      !configHide(fieldName, props)
    ) {
      errors[fieldName] = true;
    }
  });

  if (!getLocationProp(address, 'streetName')) errors.address = true;
  if (!getLocationProp(country, 'countryISO')) errors.country = true;

  const phoneNumberError = checkPhoneNumber(phoneNumber, m);

  if (phoneNumberError) errors.phoneNumber = phoneNumberError;

  return errors;
}

/**
 * @typedef {object} PersonalDetailsFormProps
 * @property {boolean} [isSubscribePage=false] - old register
 * @property {boolean} [isCreationPage=false] - new register
 */
class PersonalDetailsForm extends Component {
  constructor(props) {
    super(props);
    this.formApiRef = createRef();
    this.lastSubmitFailed = false;
    this.settings();
    this.bindFunctions();
  }

  settings() {
    this.addressSearchType = ['address'];
    this.countrySearchType = ['country'];
    this.maxDate = moment().subtract(18, 'years').toDate();
  }

  bindFunctions() {
    this.handleAddressChange = this.handleAddressChange.bind(this);
    this.handleSubmitFailedChange = this.handleSubmitFailedChange.bind(this);
    this.renderAddressField = this.renderAddressField.bind(this);
    this.renderCivilityField = this.renderCivilityField.bind(this);
    this.renderProfileAddressField = this.renderProfileAddressField.bind(this);
    this.setAcceptTermsSubmit = this.setAcceptTermsSubmit.bind(this);
  }

  setAcceptTermsSubmit(submit) {
    this.termsSubmit = submit;
  }

  handleSubmitFailedChange({ submitFailed }) {
    if (submitFailed && !this.lastSubmitFailed) {
      scrollToErrorInfo();
    }

    this.lastSubmitFailed = submitFailed;
  }

  handleAddressChange(address) {
    const formApi = this.formApiRef.current;

    if (address && formApi) {
      const { country, postalCode, city, countryISO } = address;

      if (postalCode) formApi.change('postalCode', postalCode);
      if (city) formApi.change('city', city);
      if (country && countryISO) {
        formApi.change('country', { value: country, countryISO });
      }
    }
  }

  renderCivilityField(props) {
    return withError(profileRadioGroup)(props);
  }

  renderProfileAddressField({ input, onAddressChange, ...props }) {
    const wrappedInput = onAddressChange
      ? {
          ...input,
          onChange: (value) => {
            input.onChange(value);
            onAddressChange(value);
          }
        }
      : input;

    return withError((fieldProps) =>
      profileAddressField({ ...fieldProps, input: wrappedInput })
    )(props);
  }

  renderAddressField(props) {
    return this.renderProfileAddressField({
      ...props,
      onAddressChange: this.handleAddressChange
    });
  }

  getSubmitButtonLabel() {
    const { isSubscribePage, m } = this.props;

    return isSubscribePage ? t(m, 'generic.next') : t(m, 'generic.save');
  }

  displayBackButton() {
    const { isSubscribePage } = this.props;

    if (!isSubscribePage) {
      return (
        <IndexLink to="/profile" className="scope-content-header-button-return">
          <ReactSVG className="react-svg" src={arrowLeftSrc} />
        </IndexLink>
      );
    }
  }

  getEmailField() {
    const { m } = this.props;

    if (!this.props.isSubscribePage) {
      return (
        <Field
          name="login"
          disabled={isReadOnlyField('login', this.props)}
          label={t(m, 'generic.email')}
          placeholder={t(m, 'generic.email')}
          component={PureTextField}
        />
      );
    }
  }

  showField(name, fieldValues) {
    const { [name]: fieldValue } = fieldValues || {};

    return !(
      (!fieldValue && isReadOnlyField(name, this.props)) ||
      configSubscribeHide(name, this.props) ||
      configHide(name, this.props)
    );
  }

  getGenderAvatar(fieldValues) {
    const { civility } = fieldValues || {};
    const { icons } = this.props.theme;
    const { avatarMen, avatarWomen } = icons || {};

    if (avatarMen) {
      if (civility === CIVILITY_MRS) return avatarWomen;
      else return avatarMen;
    }
    return pageIconSrc;
  }

  render() {
    const { userDataUpdating, m, initialValues } = this.props;

    return (
      <Form
        onSubmit={(values) => {
          if (this.termsSubmit) {
            this.termsSubmit((terms) => this.props.onSubmit(values, terms));
          } else {
            this.props.onSubmit(values);
          }
        }}
        initialValues={initialValues}
        validate={(values) => validate(values, this.props)}
      >
        {({ form, handleSubmit, values }) => {
          this.formApiRef.current = form;
          return (
            <form className="personal-details-page" onSubmit={handleSubmit} noValidate>
              <FormSpy
                subscription={{ submitFailed: true }}
                onChange={this.handleSubmitFailedChange}
              />
              <div className="scope-content-header">
                <img
                  className="scope-content-header-picture"
                  src={this.getGenderAvatar(values)}
                  alt="page icon"
                />
                <div className="scope-content-header-title-container">
                  {this.displayBackButton()}
                  <div className="scope-content-header-title">
                    {t(m, 'profile.section.personal.info')}
                  </div>
                </div>
              </div>
              <div className="scope-content-body">
                <div className="scope-section">
                  <div className="scope-section-fields">
                    {this.showField('civility', values) && (
                      <div className="scope-field-container">
                        <div className="scope-field-label">{t(m, 'label.civility')}</div>
                        <Field
                          name="civility"
                          disabled={isReadOnlyField('civility', this.props)}
                          component={this.renderCivilityField}
                          options={profileHonorificOptions(m)}
                        />
                      </div>
                    )}
                    {this.showField('firstName', values) && (
                      <Field
                        name="firstName"
                        disabled={isReadOnlyField('firstName', this.props)}
                        label={t(m, 'generic.first.name')}
                        placeholder={t(m, 'generic.first.name')}
                        mandatory
                        component={PureTextField}
                      />
                    )}
                    {this.showField('lastName', values) && (
                      <Field
                        name="lastName"
                        disabled={isReadOnlyField('lastName', this.props)}
                        label={t(m, 'generic.last.name')}
                        placeholder={t(m, 'generic.last.name')}
                        mandatory
                        component={PureTextField}
                      />
                    )}
                    {this.showField('login', values) && this.getEmailField()}
                    {this.showField('address', values) && (
                      <div className="scope-field-container">
                        <div className="scope-field-label">
                          {t(m, 'profile.label.street.field')} *
                        </div>
                        <Field
                          name="address"
                          searchType={this.addressSearchType}
                          disabled={isReadOnlyField('address', this.props)}
                          component={this.renderAddressField}
                          placeholder={t(m, 'placeholder.search')}
                          isStreet
                        />
                      </div>
                    )}
                    {this.showField('city', values) && (
                      <Field
                        name="city"
                        disabled={isReadOnlyField('city', this.props)}
                        label={t(m, 'generic.city')}
                        placeholder={t(m, 'generic.city')}
                        mandatory
                        component={PureTextField}
                      />
                    )}
                    {this.showField('postalCode', values) && (
                      <Field
                        name="postalCode"
                        disabled={isReadOnlyField('postalCode', this.props)}
                        label={t(m, 'generic.postal.code')}
                        placeholder={t(m, 'generic.postal.code')}
                        mandatory
                        component={PureTextField}
                      />
                    )}
                    {this.showField('country', values) && (
                      <div className="scope-field-container">
                        <div className="scope-field-label">
                          {t(m, 'generic.country')} *
                        </div>
                        <Field
                          name="country"
                          disabled={isReadOnlyField('country', this.props)}
                          searchType={this.countrySearchType}
                          showLocationButton={false}
                          component={this.renderProfileAddressField}
                          placeholder={t(m, 'placeholder.search')}
                          isValue
                        />
                      </div>
                    )}
                    {this.showField('phoneNumber', values) && (
                      <Field
                        name="phoneNumber"
                        label={t(m, 'generic.mobile.phone')}
                        mandatory
                        component={PurePhoneNumberField}
                      />
                    )}
                    {this.showField('birthDate', values) && (
                      <Field
                        name="birthDate"
                        disabled={isReadOnlyField('birthDate', this.props)}
                        placeholder={t(m, 'label.birth.day')}
                        label={t(m, 'label.birth.day')}
                        maxDate={this.maxDate}
                        mandatory
                        component={PureDateField}
                      />
                    )}
                    {this.props.isCreationPage && (
                      <AcceptTermsForm
                        isCreationPage
                        passFormSubmit={this.setAcceptTermsSubmit}
                        initialValues={this.props.acceptTermsInitialValues}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="scope-content-footer">
                <button
                  type="submit"
                  className={classnames('scope-submit-button', 'primary-btn', {
                    'sub-class-disabled': userDataUpdating
                  })}
                >
                  {this.getSubmitButtonLabel()}
                </button>
              </div>
            </form>
          );
        }}
      </Form>
    );
  }
}

PersonalDetailsForm.defaultProps = {
  isSubscribePage: false, // old register
  isCreationPage: false // new register
};

const mapStateToProps = (state, props) => {
  const { userDataUpdating } = state.user;
  const data = {
    userDataUpdating,
    m: messagesSelector(state),
    userData: userDataSelector(state),
    brandCompanyConfig: personalDetailsConfigSelector(state),
    brandCompanySubscribeConfig: personalDetailsSubscribeConfigSelector(state),
    isProfileIncompleteUser: isProfileIncompleteUserSelector(state),
    theme: currentThemeSelector(state)
  };

  if (props.isCreationPage) {
    data.acceptTermsInitialValues = acceptTermsInitialValuesSelector(state);
  }
  return data;
};

export default compose(connect(mapStateToProps))(PersonalDetailsForm);
